/**
 * Widget Studio → Appearance → Font. Mirrors the backend allowlist (`WidgetFont` in
 * `backend/app/core/constants/channels.py`): the stored value is one of these ids, never a family
 * name or a URL, so nothing a tenant types can reach the stylesheet link or a `font-family` rule.
 *
 * - `website`: inherit the host page's font (no download).
 * - `manrope`: the shipped default (existing widgets); the panel brings its own `@font-face`.
 * - the rest: Google Fonts, loaded by the loader as one `<link>` in the HOST document head
 *   (shadow roots can use fonts the host document has loaded) with a system fallback stack, so a
 *   CSP that blocks Google Fonts still renders a clean sans-serif.
 */
export const WIDGET_FONT_IDS = ["website", "manrope", "inter", "poppins", "roboto", "open-sans", "lato", "montserrat", "nunito"] as const;
export type WidgetFontId = (typeof WIDGET_FONT_IDS)[number];

/** Existing widgets (no stored font) keep Manrope. */
export const WIDGET_DEFAULT_FONT: WidgetFontId = "manrope";

export const WIDGET_FONT_FALLBACK_STACK = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
export const GOOGLE_FONTS_CSS_URL = "https://fonts.googleapis.com/css2";

interface GoogleFont {
  /** CSS family name. */
  family: string;
  /** Google Fonts `family=` value: name plus the weights the widget uses (400-800 where offered). */
  query: string;
}

/** Families fetched from Google Fonts. Weights are limited to what each family offers (a missing weight is a 400 from the API). */
const GOOGLE_FONTS: Partial<Record<WidgetFontId, GoogleFont>> = {
  inter: { family: "Inter", query: "Inter:wght@400;500;600;700;800" },
  poppins: { family: "Poppins", query: "Poppins:wght@400;500;600;700;800" },
  roboto: { family: "Roboto", query: "Roboto:wght@400;500;700;900" },
  "open-sans": { family: "Open Sans", query: "Open+Sans:wght@400;500;600;700;800" },
  lato: { family: "Lato", query: "Lato:wght@400;700;900" },
  montserrat: { family: "Montserrat", query: "Montserrat:wght@400;500;600;700;800" },
  nunito: { family: "Nunito", query: "Nunito:wght@400;500;600;700;800" },
};

/** The family each option is shown in inside Widget Studio's picker (`null` = no own face). */
export function widgetFontFamily(id: WidgetFontId): string | null {
  if (id === "manrope") return "Manrope";
  return GOOGLE_FONTS[id]?.family ?? null;
}

export function isWidgetFontId(value: unknown): value is WidgetFontId {
  return WIDGET_FONT_IDS.some((id) => id === value);
}

/** Anything outside the allowlist (older config, tampered attribute) falls back to the default. */
export function toWidgetFontId(value: unknown): WidgetFontId {
  return isWidgetFontId(value) ? value : WIDGET_DEFAULT_FONT;
}

/** `font-family` value for a Google font with the system fallback; null when the id needs none. */
export function widgetFontStack(id: WidgetFontId): string | null {
  const font = GOOGLE_FONTS[id];
  return font ? `"${font.family}", ${WIDGET_FONT_FALLBACK_STACK}` : null;
}

/** Stylesheet URL for a Google font (`display=swap`); null for `website` and `manrope`. */
export function widgetFontHref(id: WidgetFontId): string | null {
  const font = GOOGLE_FONTS[id];
  return font ? `${GOOGLE_FONTS_CSS_URL}?family=${font.query}&display=swap` : null;
}

const LINK_ID_PREFIX = "sagot-gfont-";

/**
 * Adds the Google Fonts `<link rel="stylesheet">` for `id` to `doc.head`, once per font and
 * document. Returns whether a font stylesheet is (now) present.
 */
export function ensureWidgetFontStylesheet(doc: Document, id: WidgetFontId): boolean {
  const href = widgetFontHref(id);
  if (!href) return false;
  const linkId = `${LINK_ID_PREFIX}${id}`;
  if (doc.getElementById(linkId)) return true;
  const link = doc.createElement("link");
  link.id = linkId;
  link.rel = "stylesheet";
  link.href = href;
  doc.head.append(link);
  return true;
}
